import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './RevvyAdminTab.css';

const RevvyAdminTab = () => {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedState, setSelectedState] = useState(null);
  const [editingState, setEditingState] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin/revvy/states', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStates(response.data.states || []);
    } catch (err) {
      console.error('Error fetching Revvy states:', err);
      setError('Failed to load Revvy states');
    } finally {
      setLoading(false);
    }
  };

  const handleStateSelect = (state) => {
    setSelectedState(state);
    setEditingState({ ...state });
  };

  const handleSave = async () => {
    if (!editingState) return;

    try {
      setError('');
      const token = localStorage.getItem('token');
      await axios.put('/api/admin/revvy/states', editingState, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchStates();
      setSelectedState(editingState);
      alert('State updated successfully!');
    } catch (err) {
      console.error('Error saving state:', err);
      setError(err.response?.data?.message || 'Failed to save state');
    }
  };

  const handleDelete = async () => {
    if (!selectedState) return;

    if (!window.confirm(`Are you sure you want to delete the state "${selectedState.label}" (${selectedState.id})? This action cannot be undone.`)) {
      return;
    }

    try {
      setError('');
      const token = localStorage.getItem('token');
      await axios.delete(`/api/admin/revvy/states?id=${selectedState.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchStates();
      setSelectedState(null);
      setEditingState(null);
      alert('State deleted successfully!');
    } catch (err) {
      console.error('Error deleting state:', err);
      setError(err.response?.data?.message || 'Failed to delete state');
    }
  };

  const handleSeed = async () => {
    if (!window.confirm('This will create or update all initial Revvy states. Continue?')) {
      return;
    }

    try {
      setSeeding(true);
      setError('');
      setSeedResult(null);
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/admin/revvy/seed', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeedResult(response.data);
      await fetchStates();
      alert(`Seeded successfully! Created: ${response.data.created}, Updated: ${response.data.updated}`);
    } catch (err) {
      console.error('Error seeding states:', err);
      setError(err.response?.data?.message || 'Failed to seed states');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="revvy-admin-tab">
        <div className="loading">Loading Revvy states...</div>
      </div>
    );
  }

  return (
    <div className="revvy-admin-tab">
      <div className="revvy-admin-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>Revvy State Management</h2>
            <p>Manage Revvy states, transitions, and messages. This is where you'll build and edit Revvy's flow.</p>
          </div>
          <button
            onClick={handleSeed}
            disabled={seeding}
            className="btn-seed"
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: seeding ? 'not-allowed' : 'pointer',
              opacity: seeding ? 0.6 : 1
            }}
          >
            {seeding ? 'Seeding...' : 'Seed Initial States'}
          </button>
        </div>
      </div>

      {error && (
        <div className="revvy-admin-error">
          {error}
        </div>
      )}

      {seedResult && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.2)',
          border: '1px solid rgba(16, 185, 129, 0.5)',
          color: '#6ee7b7',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <strong>Seed Complete:</strong> Created {seedResult.created} states, Updated {seedResult.updated} states
          {seedResult.errors > 0 && `, ${seedResult.errors} errors`}
        </div>
      )}

      <div className="revvy-admin-content">
        <div className="revvy-states-list">
          <h3>States ({states.length})</h3>
          <div className="states-grid">
            {states.map((state) => (
              <div
                key={state.id}
                className={`state-card ${selectedState?.id === state.id ? 'selected' : ''}`}
                onClick={() => handleStateSelect(state)}
              >
                <div className="state-card-header">
                  <h4>{state.label}</h4>
                  <span className="state-id">{state.id}</span>
                </div>
                <p className="state-description">{state.description || 'No description'}</p>
                <div className="state-meta">
                  <span>{state.actions?.length || 0} actions</span>
                  <span>{state.transitions?.length || 0} transitions</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedState && editingState && (
          <div className="revvy-state-editor">
            <h3>Edit State: {editingState.label}</h3>
            
            <div className="editor-section">
              <label>State ID</label>
              <input
                type="text"
                value={editingState.id}
                disabled
                className="editor-input"
              />
            </div>

            <div className="editor-section">
              <label>Label</label>
              <input
                type="text"
                value={editingState.label}
                onChange={(e) => setEditingState({ ...editingState, label: e.target.value })}
                className="editor-input"
              />
            </div>

            <div className="editor-section">
              <label>Description</label>
              <textarea
                value={editingState.description || ''}
                onChange={(e) => setEditingState({ ...editingState, description: e.target.value })}
                className="editor-textarea"
                rows="3"
              />
            </div>

            <div className="editor-section">
              <label>Revvy Message</label>
              <textarea
                value={editingState.revvy_message}
                onChange={(e) => setEditingState({ ...editingState, revvy_message: e.target.value })}
                className="editor-textarea"
                rows="4"
              />
            </div>

            <div className="editor-section">
              <label>Actions ({editingState.actions?.length || 0})</label>
              <div className="editor-json">
                <pre>{JSON.stringify(editingState.actions || [], null, 2)}</pre>
              </div>
              <small>Edit actions in JSON format (coming soon)</small>
            </div>

            <div className="editor-section">
              <label>Transitions ({editingState.transitions?.length || 0})</label>
              <div className="editor-json">
                <pre>{JSON.stringify(editingState.transitions || [], null, 2)}</pre>
              </div>
              <small>Edit transitions in JSON format (coming soon)</small>
            </div>

            <div className="editor-actions">
              <button onClick={handleSave} className="btn-save">
                Save Changes
              </button>
              <button onClick={() => setEditingState(null)} className="btn-cancel">
                Cancel
              </button>
              <button 
                onClick={handleDelete} 
                className="btn-delete"
                style={{ marginLeft: 'auto' }}
              >
                Delete State
              </button>
            </div>
          </div>
        )}

        {!selectedState && (
          <div className="revvy-state-placeholder">
            <p>Select a state from the list to edit</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RevvyAdminTab;
